import {
  ITableColumn,
  ITableDataItem,
  formatDate,
} from "@inceptionbg/ui-components";
import { FC, useCallback, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FarmContext } from "../../AppContent";
import { Table } from "../../Components/CustomIUI/Table/Table";
import {
  addConsentClient,
  getConsentClients,
  updateConsentClient,
} from "../../repos/ConsentRepo";
import { InfoBox } from "../../Elements/Alert/InfoBox";
import { Section } from "../../Components/Section";
import { Checkbox } from "../../Elements/Checkbox";
import { Loader } from "../../Elements/Loader";

const IndividualConsentManagementPage: FC = () => {
  const [data, setData] = useState<ITableDataItem[]>([]);
  const [limit, setLimit] = useState(10);
  const [offset, setOffset] = useState(0);
  const [totalRows, setTotalRows] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const { t } = useTranslation();
  const farmUuid = useContext(FarmContext).farm?.uuid;

  const handleGetConsentClients = useCallback(() => {
    if (farmUuid) {
      setIsLoading(true);
      getConsentClients(farmUuid, { limit, offset }, ["CLIENT"])
        .then(({ clients, totalRows }) => {
          setData(
            clients.map((client) => ({
              uuid: client.uuid,
              cells: {
                clientName: { value: client.client.ownerName || "" },
                purpose: { value: client.client.purpose || "" },
                startDate: {
                  value: client.startDate ? formatDate(client.startDate) : "",
                },
                endDate: {
                  value: client.endDate ? formatDate(client.endDate) : "",
                },
                active: {
                  value: (
                    <Checkbox
                      selected={client.active ?? false}
                      setSelected={(checked) => {
                        if (!!client.uuid) {
                          updateConsentClient(client.uuid, checked)
                            .then(() => {
                              handleGetConsentClients();
                            })
                            .catch(() => {});
                        } else {
                          addConsentClient(farmUuid, client)
                            .then(() => {
                              handleGetConsentClients();
                            })
                            .catch(() => {});
                        }
                      }}
                    />
                  ),
                },
              },
            })) as ITableDataItem[]
          );
          setTotalRows(totalRows);
        })
        .catch(() => {})
        .finally(() => setIsLoading(false));
    }
  }, [farmUuid, limit, offset]);

  useEffect(handleGetConsentClients, [handleGetConsentClients]);

  const columns: ITableColumn[] = [
    { id: "clientName", label: t("ConsentClientName") },
    { id: "purpose", label: t("ConsentClientPurpose") },
    { id: "startDate", label: t("ConsentStartDate") },
    { id: "endDate", label: t("ConsentEndDate") },
    { id: "active", label: t("ConsentActive"), align: "center" },
  ];

  return (
    <Section title={t("IndividualConsentManagementTitle")}>
      <InfoBox
        title={t("IndividualConsentManagementInfoTitle")}
        desc={t("IndividualConsentManagementInfoDesc")}
        light
      />
      <Loader isLoading={isLoading}>
        <Table
          columns={columns}
          data={data}
          serverSidePagination={{
            limit,
            offset,
            setLimit,
            setOffset,
            totalRows,
          }}
          footerAction={{
            onClick: handleGetConsentClients,
          }}
        />
      </Loader>
    </Section>
  );
};

export default IndividualConsentManagementPage;
